import { useState } from 'react';
import { Navigate } from 'react-router-dom';

import { useAuthStore } from '@/features/auth/store/authStore.js';

import { useAdminUsers, useCreateUser } from '../hooks/useAdminUsers.js';

export function AdminPage(): JSX.Element {
  const user = useAuthStore((s) => s.user);
  const { data, isLoading } = useAdminUsers();
  const createMutation = useCreateUser();
  const [form, setForm] = useState({
    email: '',
    password: '',
    displayName: '',
    role: 'user' as 'user' | 'superadmin',
  });
  const [feedback, setFeedback] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Guard côté front — la route admin n'est censée s'afficher que pour un
  // superadmin, mais on protège aussi ici si quelqu'un navigue directement.
  if (user && user.role !== 'superadmin') {
    return <Navigate to="/" replace />;
  }

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    setFeedback(null);
    setError(null);
    createMutation.mutate(form, {
      onSuccess: (res) => {
        setFeedback(`Compte créé : ${res.user.email}`);
        setForm({ email: '', password: '', displayName: '', role: 'user' });
      },
      onError: (err: unknown) => {
        const msg =
          err && typeof err === 'object' && 'response' in err
            ? ((err as { response?: { data?: { message?: string } } }).response
                ?.data?.message ?? 'Erreur')
            : 'Erreur';
        setError(msg);
      },
    });
  };

  return (
    <div className="flex flex-col gap-8">
      <header>
        <h1 className="font-serif text-3xl font-semibold text-brown md:text-4xl xl:text-5xl">
          Administration
        </h1>
        <p className="mt-1 font-script text-lg text-brown-soft xl:text-xl">
          gestion des comptes 🛠️
        </p>
      </header>

      <div className="grid gap-6 lg:grid-cols-2 lg:gap-8">
        {/* Create user */}
        <section className="rounded-3xl bg-paper p-6 shadow-md">
          <h2 className="font-serif text-xl font-semibold text-brown">
            Créer un compte
          </h2>
          <p className="mt-1 text-sm text-brown-soft">
            Tu fixes le mot de passe initial — le user pourra ensuite le
            changer.
          </p>
          <form onSubmit={handleSubmit} className="mt-5 flex flex-col gap-4">
            <div>
              <label className="font-serif text-sm font-semibold text-brown" htmlFor="admin-email">
                Email
              </label>
              <input
                id="admin-email"
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                className="mt-1.5 w-full rounded-full bg-cream-light px-4 py-2.5 text-sm text-brown ring-1 ring-cream-dark focus:outline-none focus:ring-terracotta"
              />
            </div>
            <div>
              <label className="font-serif text-sm font-semibold text-brown" htmlFor="admin-name">
                Nom affiché
              </label>
              <input
                id="admin-name"
                type="text"
                required
                maxLength={50}
                value={form.displayName}
                onChange={(e) =>
                  setForm((f) => ({ ...f, displayName: e.target.value }))
                }
                className="mt-1.5 w-full rounded-full bg-cream-light px-4 py-2.5 text-sm text-brown ring-1 ring-cream-dark focus:outline-none focus:ring-terracotta"
              />
            </div>
            <div>
              <label className="font-serif text-sm font-semibold text-brown" htmlFor="admin-password">
                Mot de passe initial
              </label>
              <input
                id="admin-password"
                type="text"
                required
                minLength={8}
                value={form.password}
                onChange={(e) =>
                  setForm((f) => ({ ...f, password: e.target.value }))
                }
                placeholder="Min. 8 caractères"
                className="mt-1.5 w-full rounded-full bg-cream-light px-4 py-2.5 text-sm text-brown ring-1 ring-cream-dark focus:outline-none focus:ring-terracotta"
              />
            </div>
            <div>
              <p className="font-serif text-sm font-semibold text-brown">Rôle</p>
              <div className="mt-1.5 flex gap-2">
                {(['user', 'superadmin'] as const).map((r) => (
                  <button
                    key={r}
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, role: r }))}
                    aria-pressed={form.role === r}
                    className={`rounded-full px-4 py-2 text-xs font-semibold transition-colors ${
                      form.role === r
                        ? 'bg-terracotta text-cream-light'
                        : 'bg-cream-light text-brown ring-1 ring-cream-dark hover:bg-cream-dark'
                    }`}
                  >
                    {r === 'user' ? 'Utilisateur' : 'Superadmin'}
                  </button>
                ))}
              </div>
            </div>
            <button
              type="submit"
              disabled={createMutation.isPending}
              className="mt-2 self-start rounded-full bg-terracotta px-5 py-2.5 text-sm font-semibold text-cream-light hover:bg-terracotta-dark disabled:opacity-50"
            >
              {createMutation.isPending ? 'Création…' : 'Créer le compte'}
            </button>
            {feedback && (
              <p className="text-sm font-semibold text-sage-dark">{feedback}</p>
            )}
            {error && (
              <p className="text-sm font-semibold text-red-500">{error}</p>
            )}
          </form>
        </section>

        {/* Users list */}
        <section className="rounded-3xl bg-paper p-6 shadow-md">
          <h2 className="font-serif text-xl font-semibold text-brown">
            Comptes existants
          </h2>
          {isLoading ? (
            <p className="mt-3 text-sm text-brown-soft">Chargement…</p>
          ) : (
            <ul className="mt-4 flex flex-col gap-2">
              {data?.items.map((u) => (
                <li
                  key={u.id}
                  className="flex items-center gap-3 rounded-2xl bg-cream-light px-3 py-2.5"
                >
                  <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-rose to-terracotta text-sm font-semibold text-cream-light">
                    {u.displayName.charAt(0).toUpperCase()}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-serif text-sm font-semibold text-brown">
                      {u.displayName}
                    </p>
                    <p className="truncate text-xs text-brown-soft">{u.email}</p>
                  </div>
                  <span
                    className={`flex-shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${
                      u.role === 'superadmin'
                        ? 'bg-brown text-cream-light'
                        : 'bg-cream-dark text-brown-soft'
                    }`}
                  >
                    {u.role === 'superadmin' ? 'Admin' : 'User'}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}
